import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import i18n from 'i18n-js';
import {
  Alert, Button, Card, Col, Flex, Input, Pagination, Row, Select, Spin, Typography,
} from '@duxton-web/ui';
import { SearchUniversal } from '@duxton-web/icons';

import { searchUCDCampaignGroups } from 'omega-rtc/actions/ucd';
import { UCD_ROUTES_LINK } from 'omega-rtc/constants/ucd';
import type { CampaignGroupSummary } from 'omega-rtc/types/ts/journey-campaign-group-search';
import {
  CAMPAIGN_GROUP_QUERY_KEYS,
  generateSearchCampaignGroupRequestBody,
  getDistinctCampaignGroupCountries,
  updateCampaignGroupSearchParams,
} from 'omega-rtc/utils/campaign-group-search';
import type { CampaignGroupSearchParams } from 'omega-rtc/utils/campaign-group-search';
import { getCountryFlag } from 'omega-rtc/utils/country-flag';
import NoContentPlaceHolder from 'omega-rtc/components/form/ucd/common/NoContentPlaceHolder';
import NoProjectsPlaceholderIcon from 'img/ucd_no_projects_placeholder.svg';

const label = (key: string, values = {}) => i18n.t(`campaign_group_search.${key}`, values);
const keys = CAMPAIGN_GROUP_QUERY_KEYS;

export const CampaignGroupCard = ({ group }: { group: CampaignGroupSummary }) => {
  const audienceKey = group.audienceType.toLowerCase();
  const journeyKey = group.journeyType.toLowerCase();
  return (
    <Col xs={24} sm={12} lg={8} style={{ minWidth: 0 }}>
      <Link
        to={UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_GROUP_VIEW(encodeURIComponent(group.campaignGroupID))}
        style={{ display: 'block', height: '100%' }}
      >
        <Card hoverable style={{ height: '100%' }}>
          <Typography.Title level={5} style={{ overflowWrap: 'anywhere' }}>{group.name}</Typography.Title>
          <Flex vertical gap={8}>
            <Typography.Text>{group.groupType === 'JOURNEY' ? label('journey') : group.groupType}</Typography.Text>
            <Typography.Text type="secondary">
              {['pax', 'dax', 'mex'].includes(audienceKey) ? label(audienceKey) : group.audienceType}
              {' · '}
              {['single_touch', 'multi_touch'].includes(journeyKey) ? label(journeyKey) : group.journeyType}
            </Typography.Text>
            <Typography.Text>{label('campaign_count', { count: Number(group.campaignCount || 0) })}</Typography.Text>
            <Flex wrap="wrap" gap={8}>
              {getDistinctCampaignGroupCountries(group.countries).map((country) => (
                <span key={String(country.countryID)} role="img" aria-label={country.countryCode || `Country ${country.countryID}`}>
                  {getCountryFlag(country.countryCode || '')}
                </span>
              ))}
            </Flex>
          </Flex>
        </Card>
      </Link>
    </Col>
  );
};

interface Props {
  searchParams: CampaignGroupSearchParams;
  router: { push: (location: { pathname: string; query: CampaignGroupSearchParams }) => void };
}

const CampaignGroupSearchView = ({ searchParams, router }: Props) => {
  const dispatch = useDispatch();
  const listing = useSelector((state: any) => state.ucd.campaignGroupSearch);
  const request = generateSearchCampaignGroupRequestBody(searchParams);
  const searchKey = JSON.stringify(request);
  const urlSearch = searchParams[keys.search] || '';
  const [searchText, setSearchText] = useState(urlSearch);
  useEffect(() => setSearchText(urlSearch), [urlSearch]);
  useEffect(() => {
    dispatch(searchUCDCampaignGroups(JSON.parse(searchKey)));
  }, [dispatch, searchKey]);

  const changeFilter = (key: string, value?: string | number | boolean) => router.push({
    pathname: UCD_ROUTES_LINK.HOME(),
    query: updateCampaignGroupSearchParams(searchParams, key, value),
  });
  const audienceFilter = request.filters.find((filter) => filter.lhs === 'audienceType');
  const audience = audienceFilter && audienceFilter.rhs;
  const journeyTypeFilter = request.filters.find((filter) => filter.lhs === 'journeyType');
  const journeyType = journeyTypeFilter && journeyTypeFilter.rhs;
  const loading = !listing || listing.searchKey !== searchKey || listing.isPending;

  const renderResults = () => {
    if (loading) {
      return (
        <Flex role="status" aria-label={label('loading')} justify="center" align="center" style={{ minHeight: '50vh' }}>
          <Spin />
        </Flex>
      );
    }
    if (listing.error) {
      return (
        <Alert
          type="error"
          showIcon
          message={label('error')}
          description={listing.error}
          style={{ marginTop: 24 }}
          action={<Button onClick={() => dispatch(searchUCDCampaignGroups(request))}>{label('retry')}</Button>}
        />
      );
    }
    return (
      <Flex vertical style={{ marginTop: 24 }}>
        {listing.results.length ? (
          <Row gutter={[16, 16]}>
            {listing.results.map((group: CampaignGroupSummary) => (
              <CampaignGroupCard key={group.campaignGroupID} group={group} />
            ))}
          </Row>
        ) : (
          <div style={{ padding: '48px 0' }}>
            <NoContentPlaceHolder icon={NoProjectsPlaceholderIcon} content={label('empty')} subContent={label('empty_hint')} />
          </div>
        )}
        {(listing.totalMatch > 0 || request.page > 1) && (
          <Flex justify="flex-end" style={{ marginTop: 24 }}>
            <Pagination
              current={request.page}
              pageSize={request.pageSize}
              total={listing.totalMatch}
              showSizeChanger={false}
              onChange={(page) => changeFilter(keys.page, page)}
              showTotal={(total, range) => label('items', { start: range[0], end: range[1], total })}
            />
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <div>
      <Flex wrap="wrap" gap={12} align="center" style={{ marginTop: 16 }}>
        <Input
          aria-label={label('search')}
          placeholder={label('search')}
          value={searchText}
          prefix={<SearchUniversal />}
          allowClear
          style={{ width: 280, maxWidth: '100%' }}
          onChange={(event) => {
            setSearchText(event.target.value);
            if (!event.target.value) changeFilter(keys.search, '');
          }}
          onPressEnter={() => changeFilter(keys.search, searchText.trim())}
        />
        <Select
          aria-label={label('type')}
          value="JOURNEY"
          style={{ width: 140 }}
          options={[{ label: label('journey'), value: 'JOURNEY' }]}
          onChange={(value) => changeFilter(keys.type, value)}
        />
        <Select
          aria-label={label('journey_type')}
          placeholder={label('journey_type')}
          value={journeyType || ''}
          style={{ width: 180 }}
          onChange={(value) => changeFilter(keys.journeyType, value)}
          options={[
            { label: label('all_journey_types'), value: '' },
            { label: label('single_touch'), value: 'SINGLE_TOUCH' },
            { label: label('multi_touch'), value: 'MULTI_TOUCH' },
          ]}
        />
        <Button
          aria-pressed={request.ownedByMe}
          type={request.ownedByMe ? 'primary' : 'default'}
          onClick={() => changeFilter(keys.ownership, !request.ownedByMe)}
        >
          {label('ownership')}
        </Button>
        <Select
          aria-label={label('audience')}
          placeholder={label('audience')}
          value={audience || ''}
          style={{ width: 160 }}
          onChange={(value) => changeFilter(keys.audience, value)}
          options={[
            { label: label('all_audiences'), value: '' },
            ...['pax', 'dax', 'mex'].map((value) => ({ label: label(value), value })),
          ]}
        />
      </Flex>
      {renderResults()}
    </div>
  );
};

export default CampaignGroupSearchView;
